import { useMemo, useRef, type ReactNode } from 'react';
import { useCapabilities } from '@lemontaigu/platform-ui';
import {
  SupabaseActivityCatalogStore,
  SupabaseMetricStore,
  SupabaseStaffingConfigStore,
  SupabaseBoardRealtime,
  SupabaseBoardStore,
  SupabaseFeedProvenanceStore,
  SupabaseBoardWriteStore,
  SupabaseChannelCurationStore,
  SupabaseRegisterWriteStore,
  SupabaseTimesheetWriteStore,
  SupabaseShiftRequestStore,
  SupabaseChannelReadStore,
  SupabaseChannelRealtime,
  SupabaseRegisterStore,
  SupabaseRosterStore,
  SupabaseTimesheetStore,
  SupabaseTrustedPersonStore,
  SupabaseTrustedPersonWriteStore,
  SupabaseDeskListStore,
} from '@lemontaigu/staff/adapters/client';
import {
  createActivityCatalogService,
  createMetricRegistryService,
  createStaffingConfigService,
  createBoardDemandService,
  createBoardPresenceService,
  createChannelCurationService,
  createRegisterService,
  createShiftRequestDecisionService,
  createTimesheetCorrectionService,
  createTrustedPersonService,
  createDeskListService,
} from '@lemontaigu/staff';
import { StaffStoresProvider } from '@lemontaigu/staff/react';
import { supabase } from './supabase.js';

/**
 * The C3 composition root for the back-office (ladder step 6) — the sibling of
 * `PmsProvider`, which already wraps this app's C4 tabs.
 *
 * Same three stores as the other two SPAs, over an ADMIN session: the operator
 * holds the `rh.*` capabilities, so RLS returns the whole team rather than one
 * employee's slice. The adapters are identical; the privilege difference lives
 * entirely in the database, which is the point of binding the client here and
 * nowhere else. (Said `is_admin()` until 2026-07-28 — migration 0064 dropped
 * that function; authorization resolves through `has_capability()` now.)
 *
 * Note this app mounts `@lemontaigu/reception`'s `Planning`, which carries its
 * OWN `BoardProvider` — nesting is harmless (the inner provider wins for that
 * subtree) and correct: that component owns its wiring so both cockpits get it
 * without each app repeating it.
 */
export function StaffProvider({ children }: { children: ReactNode }) {
  // The L2 gate's actor source (F3 increment 3). Through a ref because the
  // stores are built once while the capability set arrives asynchronously — see
  // `ActorSource`; capturing by value would freeze every gate at ANONYMOUS.
  const { actor } = useCapabilities();
  const actorRef = useRef(actor);
  actorRef.current = actor;

  const stores = useMemo(() => {
    const boardWrites = new SupabaseBoardWriteStore(supabase);
    const actorSrc = { current: () => actorRef.current };
    return {
      board: new SupabaseBoardStore(supabase),
      // M4 — bos.* mounts the same demand views, so it needs the same
      // « importé à … » stamp (RUNBOOK §20.7).
      feedProvenance: new SupabaseFeedProvenanceStore(supabase),
      roster: new SupabaseRosterStore(supabase),
      timesheet: new SupabaseTimesheetStore(supabase),
      channel: new SupabaseChannelReadStore(supabase),
      // presence takes no actor — it is identity-reachable (ladder §3.19)
      boardPresence: createBoardPresenceService(boardWrites),
      boardDemand: createBoardDemandService(boardWrites, actorSrc),
      boardRealtime: new SupabaseBoardRealtime(supabase),
      channelRealtime: new SupabaseChannelRealtime(supabase),
      // The register is bound HERE ONLY. The other two SPAs leave it undefined,
      // so `useRegister()` throws there rather than returning a port that would
      // fail at the database — RUP data has one consumer, and the wiring says so.
      register: new SupabaseRegisterStore(supabase),
      // The operator-only WRITE use cases, bound here for the same reason as
      // the register read: this is the only runtime that may reach them.
      activityCatalog: createActivityCatalogService(new SupabaseActivityCatalogStore(supabase), actorSrc),
      staffingConfig: createStaffingConfigService(new SupabaseStaffingConfigStore(supabase), actorSrc),
      metricRegistry: createMetricRegistryService(new SupabaseMetricStore(supabase), actorSrc),
      timesheetCorrection: createTimesheetCorrectionService(new SupabaseTimesheetWriteStore(supabase), actorSrc),
      // The shift-request inbox (brief §C): decide what employees send from staff.*.
      shiftRequestDecisions: createShiftRequestDecisionService(new SupabaseShiftRequestStore(supabase), actorSrc),
      // « Hors Staff » (ADR-0024): bound here only — the one runtime allowed to reach those people.
      trustedPersons: new SupabaseTrustedPersonStore(supabase),
      trustedPersonService: createTrustedPersonService(new SupabaseTrustedPersonWriteStore(supabase), actorSrc),
      // The desk list (ADR-0026): who the desk's « Qui êtes-vous ? » shows.
      deskList: createDeskListService(new SupabaseDeskListStore(supabase), actorSrc),
      registerWrite: createRegisterService(new SupabaseRegisterWriteStore(supabase), actorSrc),
      channelCuration: createChannelCurationService(new SupabaseChannelCurationStore(supabase), actorSrc),
    };
  }, []);
  return <StaffStoresProvider stores={stores}>{children}</StaffStoresProvider>;
}
